import { useCallback, useEffect, useRef } from "react";

import { useRefreshableInterval } from "@examspark/common-ui";

import { ExamState } from "../components/exam/context/examContext";
import { ItemPosition } from "../utils/sections";

// Auto-save works on two main principles:
// 1. If the user has been entering data into the same question
//    for an amount of time, save their work
// 2. if a user is quickly going from question to question, save
//    their work as they navigate.
// In both cases, we want to spare our backend from out-of-the-ordinary
// situations (user leaves their computer open / user rapidly navigating
// without entering data) by only saving if there has been a change
// to the response data (they've entered data)
// NOTE: We also ALWAYS save during submission, separately from this
export const useAutoSave = (
  state: ExamState,
  onSave: (state: ExamState) => void,
) => {
  const lastSavedState = useRef<ExamState | null>(null);
  const betterSave = useCallback(() => {
    if (state.isPaused) return;
    if (state.isCompleted) return;

    if (
      JSON.stringify(lastSavedState.current?.responses) !==
      JSON.stringify(state.responses)
    ) {
      onSave?.(state);
      lastSavedState.current = state;
    }
  }, [onSave, state]);

  // try to autosave every 30 seconds
  const [refreshSaveInterval] = useRefreshableInterval(() => {
    betterSave();
  }, 30 * 1000);

  // try to autosave on navigation
  const lastPosition = useRef<ItemPosition | null>(null);
  useEffect(() => {
    // do not autosave on first render
    if (!lastPosition.current) {
      lastPosition.current = state.position;
    } else if (state.position !== lastPosition.current) {
      betterSave();
      refreshSaveInterval();
      lastPosition.current = state.position;
    }
  }, [state.position, betterSave, refreshSaveInterval]);
};
